import { z } from "zod";
import { fetchFastApi } from "../../api/fastapi";
import { useHookCacheQuery } from "../../lib/hookCache";
import {
  AttributeTestColumnResult,
  AttributeTestColumnResultSchema,
} from "../../models/audit/attributeTestColumn";

const attributeTestResultDetailEnvelopeSchema = z.object({
  result: AttributeTestColumnResultSchema,
});

/**
 * Workpaper-lazy-load: per-attribute-test-result lazy-fetch hook for the
 * existing BE route `GET /matching/attribute-test-results/{result_id}` (the
 * heretic router is mounted at the `/matching` prefix; the route itself is
 * declared in `attribute_test_results/router.py`). The BE wraps the payload
 * as `{ result: AttributeTestColumnResult }`.
 *
 * The route is intentionally unscoped from procedure-session at the URL
 * level; the cache key still includes `psId` so the
 * `useSessionRunsTransitions` queued→ready cascade can prefix-invalidate
 * `["procedure-session", psId, "attribute-test-result"]` and wipe all
 * per-test-result entries for the session in one pass.
 *
 * Consumed by `AttributeTestAnalysis` / `useAttributeTestData` — the side
 * panel matching screenshots `06-detail-accuracy-test-panel.png` and
 * `07-detail-cutoff-test-panel.png` now hydrates only on click.
 */
export function useAttributeTestResultDetail(
  procedureSessionId: string | undefined,
  attributeTestResultId: string | undefined,
) {
  return useHookCacheQuery<AttributeTestColumnResult>({
    key: [
      "procedure-session",
      procedureSessionId ?? "",
      "attribute-test-result",
      attributeTestResultId ?? "",
    ],
    fetcher: async () => {
      const response = await fetchFastApi({
        method: "GET",
        path: `/matching/attribute-test-results/${attributeTestResultId}`,
      });
      return attributeTestResultDetailEnvelopeSchema.parse(response).result;
    },
    enabled: !!attributeTestResultId,
  });
}
